import React, { useContext, useState } from "react";
import clsx from "clsx";
import { PrivacyModeContext, TransactionListContext } from "@/contexts";
import { useAssetJourney, AssetJourneyData, JourneyNarrativeEntry } from "@/hooks/useAssetJourney";
import { ArrowRightIcon, ArrowsRightLeftIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { Callout, DHead, Lab, Pill } from "@/components/ms";

// Format timestamp to readable date, optionally with time
const formatDate = (timestamp: number, showTime = false) => {
  const date = new Date(timestamp);
  const dateStr = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  if (!showTime) return dateStr;
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateStr}, ${timeStr}`;
};

// Get set of dates that appear multiple times (need time shown)
const getDuplicateDates = (entries: { timestamp: number }[]) => {
  const dateCounts = new Map<string, number>();
  entries.forEach(e => {
    const dateKey = new Date(e.timestamp).toDateString();
    dateCounts.set(dateKey, (dateCounts.get(dateKey) || 0) + 1);
  });
  const duplicates = new Set<string>();
  dateCounts.forEach((count, dateKey) => {
    if (count > 1) duplicates.add(dateKey);
  });
  return duplicates;
};

// Format amount with appropriate precision - never use scientific notation
const formatAmount = (amount: number) => {
  if (amount === 0) return "-";
  const absAmount = Math.abs(amount);
  // For very small numbers, show full decimal representation
  if (absAmount < 0.0001 && absAmount > 0) {
    // Calculate decimal places needed (up to 18 for crypto precision)
    const decimalPlaces = Math.min(18, Math.max(8, -Math.floor(Math.log10(absAmount)) + 4));
    return amount.toFixed(decimalPlaces).replace(/\.?0+$/, '') || '0';
  }
  if (absAmount < 1) return amount.toFixed(6);
  if (absAmount < 1000) return amount.toFixed(4);
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// Narrative entry component
const NarrativeItem = ({ entry }: { entry: JourneyNarrativeEntry }) => {
  const getIcon = () => {
    switch (entry.type) {
      case "internal_transfer":
        return <ArrowsRightLeftIcon className="h-4 w-4 text-ms-link" />;
      case "buy":
        return <span className="text-ms-green">+</span>;
      case "sell":
        return <span className="text-ms-neg">-</span>;
      case "external_receive":
        return <span className="text-ms-green">↓</span>;
      case "external_send":
        return <span className="text-ms-neg">↑</span>;
      case "income":
        return <span className="text-ms-tan">★</span>;
      default:
        return <span>•</span>;
    }
  };

  return (
    <div className="flex items-start gap-2 py-1">
      <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
        {getIcon()}
      </span>
      <div className="flex-1">
        <span className="text-[11px] text-ms-mute">{entry.date}</span>
        <p className="text-[13px] text-ms-ink">{entry.text}</p>
      </div>
    </div>
  );
};

// Timeline row component - clickable to open transaction details
const TimelineRow = ({ entry, asset, privacyMode, isInternal, onRowClick, showTime }) => (
  <tr
    className={clsx(
      "cursor-pointer border-t border-ms-line transition-colors hover:bg-ms-card2",
      entry.balance < 0 && "bg-ms-neg-soft",
      isInternal && entry.balance >= 0 && "bg-ms-mint-soft"
    )}
    onClick={() => onRowClick?.(entry.ext_txn_id)}
  >
    <td className="whitespace-nowrap px-2 py-2 font-mono text-[11px] tabular-nums text-ms-mute">
      {formatDate(entry.timestamp, showTime)}
    </td>
    <td className="px-2 py-2 text-[12px] capitalize text-ms-ink">
      <span className={clsx(
        isInternal && "text-ms-link"
      )}>
        {entry.type}
        {isInternal && " ↔"}
      </span>
    </td>
    <td className="px-2 py-1.5 text-[11px] text-ms-mute">
      {entry.exchange || "-"}
    </td>
    <td className={clsx(
      "px-2 py-2 text-right font-mono text-[11px] tabular-nums",
      entry.received > 0 ? "text-ms-green" : "text-ms-dim",
      privacyMode && "blur-sm"
    )}>
      {entry.received > 0 ? `+${formatAmount(entry.received)}` : "-"}
    </td>
    <td className={clsx(
      "px-2 py-2 text-right font-mono text-[11px] tabular-nums",
      entry.spent > 0 ? "text-ms-neg" : "text-ms-dim",
      privacyMode && "blur-sm"
    )}>
      {entry.spent > 0 ? `-${formatAmount(entry.spent)}` : "-"}
    </td>
    <td className={clsx(
      "px-2 py-2 text-right font-mono text-[11px] font-medium tabular-nums",
      entry.balance < 0 ? "text-ms-neg" : "text-ms-ink",
      privacyMode && "blur-sm"
    )}>
      {formatAmount(entry.balance)} {asset}
    </td>
  </tr>
);

// Summary stats component
const JourneySummary = ({ summary, asset, privacyMode }) => (
  <div className={clsx(
    "grid grid-cols-2 gap-2 border border-t-[3px] border-ms-line border-t-ms-rule p-3 text-[11.5px] ms700:grid-cols-4",
    privacyMode && "blur-sm"
  )}>
    <div>
      <span className="text-ms-mute">Bought:</span>
      <span className="ml-1 font-mono font-medium tabular-nums text-ms-green">
        +{formatAmount(summary.total_bought)} {asset}
      </span>
    </div>
    <div>
      <span className="text-ms-mute">Sold:</span>
      <span className="ml-1 font-mono font-medium tabular-nums text-ms-neg">
        -{formatAmount(summary.total_sold)} {asset}
      </span>
    </div>
    <div>
      <span className="text-ms-mute">Transferred:</span>
      <span className="ml-1 font-mono font-medium tabular-nums text-ms-link">
        {formatAmount(summary.total_internal_transferred)} {asset}
      </span>
    </div>
    <div>
      <span className="text-ms-mute">Balance:</span>
      <span className={clsx(
        "ml-1 font-mono font-medium tabular-nums",
        summary.final_balance < 0 ? "text-ms-neg" : "text-ms-ink"
      )}>
        {formatAmount(summary.final_balance)} {asset}
      </span>
    </div>
  </div>
);

// Matched transfers section
const MatchedTransfers = ({ transfers, privacyMode }) => {
  if (!transfers || transfers.length === 0) return null;

  return (
    <div className="mt-3">
      <Lab className="mb-1.5 block">Internal transfers detected · {transfers.length}</Lab>
      <div className="space-y-1">
        {transfers.map((t, idx) => (
          <div key={idx} className="flex items-center gap-2 border-t border-ms-line py-1.5 text-[11.5px] text-ms-ink first:border-t-0">
            <span className="font-medium">{t.from_exchange}</span>
            <ArrowRightIcon className="h-3 w-3 text-ms-dim" />
            <span className="font-medium">{t.to_exchange}</span>
            <span className={clsx("font-mono tabular-nums text-ms-mute", privacyMode && "blur-sm")}>
              ({formatAmount(t.amount)})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface AssetJourneyViewProps {
  asset: string;
  errorContext?: {
    required_deposit?: number;
    balance_before_txn?: number;
    sell_amount?: number;
    has_matched_transfers?: boolean;
    matched_received?: number;
    unmatched_received?: number;
  };
}

export const AssetJourneyView: React.FC<AssetJourneyViewProps> = ({ asset, errorContext }) => {
  const { privacyMode } = useContext(PrivacyModeContext);
  const { setModalOpen, setExtnTxnId } = useContext(TransactionListContext);
  const [narrativeExpanded, setNarrativeExpanded] = useState(true);
  const { isLoading, isError, error, journeyData, refetch, isFetching } = useAssetJourney(asset);

  const handleRowClick = (extTxnId: string) => {
    if (setExtnTxnId) {
      setExtnTxnId(extTxnId);
    }
    setModalOpen?.(true);
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <Lab className="text-ms-dim">Loading asset journey…</Lab>
      </div>
    );
  }

  if (isError) {
    return (
      <Callout tone="neg">
        <b>We couldn't load this journey.</b>{" "}
        {(error as Error)?.message || "Unknown error"}
        <span className="mt-3 block">
          <Pill variant="ghost" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Pill>
        </span>
      </Callout>
    );
  }

  if (!journeyData || journeyData.timeline.length === 0) {
    return (
      <div className="p-4 text-center">
        <Lab className="text-ms-dim">No cross-wallet data found for {asset}</Lab>
      </div>
    );
  }

  const { timeline, matched_transfers, narrative, summary, asset_symbol } = journeyData;

  // Build set of dates that appear multiple times (show time for these)
  const timelineDuplicateDates = getDuplicateDates(timeline);

  // Build set of internal transfer IDs for highlighting
  const internalTxnIds = new Set<number>();
  matched_transfers.forEach((t) => {
    internalTxnIds.add(t.send_id);
    internalTxnIds.add(t.receive_id);
  });

  return (
    <div className="space-y-3">
      {/* Imbalance Info */}
      {errorContext && errorContext.required_deposit && (
        <div className="border border-l-[3px] border-ms-line border-l-ms-neg bg-ms-neg-soft p-3 text-[11.5px]">
          <Lab className="mb-1.5 block text-ms-neg">Balance imbalance detected</Lab>
          <div className={clsx("grid grid-cols-3 gap-2", privacyMode && "blur-sm")}>
            <div>
              <span className="text-ms-mute">Balance before:</span>
              <span className="ml-1 font-mono font-medium tabular-nums text-ms-ink">{formatAmount(errorContext.balance_before_txn || 0)} {asset_symbol}</span>
            </div>
            <div>
              <span className="text-ms-mute">Sell amount:</span>
              <span className="ml-1 font-mono font-medium tabular-nums text-ms-neg">{formatAmount(errorContext.sell_amount || 0)} {asset_symbol}</span>
            </div>
            <div>
              <span className="text-ms-mute">Missing:</span>
              <span className="ml-1 font-mono font-medium tabular-nums text-ms-neg">{formatAmount(errorContext.required_deposit || 0)} {asset_symbol}</span>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <JourneySummary summary={summary} asset={asset_symbol} privacyMode={privacyMode} />

      {/* Narrative - Collapsible */}
      <div>
        <button
          onClick={() => setNarrativeExpanded(!narrativeExpanded)}
          className="flex items-center gap-1 text-[13px] font-medium text-ms-ink transition-colors hover:text-ms-link"
        >
          {narrativeExpanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
          Journey for {asset_symbol}
        </button>
        {narrativeExpanded && (
          <div className="space-y-0.5 mt-2 ml-5">
            {narrative.map((entry, idx) => (
              <NarrativeItem key={idx} entry={entry} />
            ))}
          </div>
        )}
      </div>

      {/* Matched transfers */}
      <MatchedTransfers transfers={matched_transfers} privacyMode={privacyMode} />

      {/* Timeline table */}
      <div>
        <DHead
          label={`Full timeline · ${timeline.length} transactions`}
          right="All wallets"
          className="mb-1.5"
        />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr>
                {["Date", "Type", "Wallet", "In", "Out", "Balance"].map((h, i) => (
                  <th
                    key={h}
                    className={clsx(
                      "whitespace-nowrap border-b border-ms-ink px-2 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.11em] text-ms-dim",
                      i > 2 && "text-right"
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeline.map((entry, idx) => (
                <TimelineRow
                  key={entry.id || idx}
                  entry={entry}
                  asset={asset_symbol}
                  privacyMode={privacyMode}
                  isInternal={internalTxnIds.has(entry.id)}
                  onRowClick={handleRowClick}
                  showTime={timelineDuplicateDates.has(new Date(entry.timestamp).toDateString())}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
